import { Skeleton } from 'antd';

import Tooltip from 'components/tooltip';

import {
  DeadlineCellButton,
  DeadlineOverdueDot,
  DeadlinePrimaryText,
  DeadlineSecondaryDateText,
  DeadlineSecondaryRow,
  DeadlineSecondaryText,
  DeadlineText,
} from './styles';
import {
  DEADLINE_COMPLETED_STATUSES,
  formatDeadlineDateNoLeadingZero,
  getDeadlineDisplay,
} from './deadlineUtils';
import { DeadlineColumnValue } from './types';

interface DeadlineColumnProps {
  deadline: DeadlineColumnValue;
  onEdit: (userId: string) => void;
  userId: string;
  finishedDate?: string | null;
  progress?: number;
  loading?: boolean;
}

const DeadlineColumn = ({
  deadline,
  onEdit,
  userId,
  finishedDate = null,
  progress,
  loading = false,
}: DeadlineColumnProps): JSX.Element => {
  if (loading) {
    return <Skeleton.Input active size="small" style={{ width: '100%', minWidth: 120 }} />;
  }

  const { primaryText, secondaryText, showOverdueDot } = getDeadlineDisplay(deadline);
  const isCompletedStatus = !!deadline && DEADLINE_COMPLETED_STATUSES.has(deadline.status);
  const normalizedSecondaryText = secondaryText?.trim().toLowerCase();
  const isCompletedSecondaryText = Boolean(
    normalizedSecondaryText && normalizedSecondaryText.startsWith('completed')
  );
  const isCompleted = isCompletedStatus || isCompletedSecondaryText;
  const isPlaylistCompleted = progress !== undefined && progress >= 1;
  const isDisabled = isCompleted || isPlaylistCompleted;
  const completedDate = finishedDate ? formatDeadlineDateNoLeadingZero(finishedDate) : null;

  const content = (
    <DeadlineText>
      <DeadlinePrimaryText $disableHover={isDisabled}>{primaryText}</DeadlinePrimaryText>
      {secondaryText && (
        <DeadlineSecondaryRow>
          {isCompleted && completedDate ? (
            <DeadlineSecondaryText>
              completed <DeadlineSecondaryDateText>{completedDate}</DeadlineSecondaryDateText>
            </DeadlineSecondaryText>
          ) : (
            <DeadlineSecondaryText>{secondaryText}</DeadlineSecondaryText>
          )}
          {showOverdueDot && <DeadlineOverdueDot aria-hidden="true" />}
        </DeadlineSecondaryRow>
      )}
    </DeadlineText>
  );

  if (isDisabled) {
    return (
      <DeadlineCellButton as="span" data-disable-hover="true" style={{ cursor: 'default' }}>
        {content}
      </DeadlineCellButton>
    );
  }

  return (
    <Tooltip title="Manage user deadline">
      <DeadlineCellButton
        type="button"
        data-disable-hover="false"
        onClick={() => onEdit(userId)}
        aria-label={`Edit deadline for user ${userId}`}
      >
        {content}
      </DeadlineCellButton>
    </Tooltip>
  );
};

export default DeadlineColumn;
